import { AlertTriangle, GripVertical } from 'lucide-react'

import type { ScheduledBlock } from '@/types/cgt'
import { cn } from '@/lib/utils'
import { blockColor } from './blockColors'

interface ScheduleBlockProps {
  block: ScheduledBlock
  /** Hours this specific lane books (resource-grid chips show only their slice). */
  laneHours?: number
  /** Show a drag handle affordance (resource-grid chips). */
  draggable?: boolean
  className?: string
}

/** Shared presentational block — a soft card with a colored accent rail. */
export function ScheduleBlock({ block, laneHours, draggable, className }: ScheduleBlockProps) {
  const color = blockColor(block.formType)
  const hours = laneHours ?? block.durationHours
  return (
    <div
      className={cn(
        'group/blk relative flex items-stretch gap-2 overflow-hidden rounded-lg border pr-2 shadow-sm transition-all',
        'hover:-translate-y-px hover:shadow-md',
        color.soft,
        block.hasConflict && 'ring-2 ring-destructive/70 ring-offset-1',
        className,
      )}
      title={block.conflictReasons.join('\n') || block.label}
    >
      <span className={cn('w-1.5 shrink-0', color.bar)} aria-hidden />
      <div className="min-w-0 flex-1 py-1.5">
        <div className="flex items-center gap-1 text-xs font-semibold leading-tight">
          {block.hasConflict && (
            <AlertTriangle className="size-3 shrink-0 text-destructive" aria-hidden />
          )}
          <span className="truncate">{block.label}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium tabular-nums shadow-sm">
            {hours} hrs
          </span>
        </div>
      </div>
      {draggable && (
        <GripVertical
          className="absolute right-1 top-1 size-3 text-current opacity-0 transition-opacity group-hover/blk:opacity-40"
          aria-hidden
        />
      )}
    </div>
  )
}
